import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, Check, MoreVertical, User, Settings, LogOut, Eye, Shield } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useCurrentUser } from '@/hooks/auth';
import { useIsAdmin } from '@/hooks/auth/useIsAdmin';
import { useSupervisionAccess } from '@/pages/supervisao/hooks/useSupervisionAccess';
import { OrganizationsModal } from './OrganizationsModal';
import { supabase } from '@/integrations/supabase/client';
import { cleanupAuthState } from '@/lib/auth-cleanup';
import { cn } from '@/lib/utils';

export const OrganizationSwitcher = () => {
  const navigate = useNavigate();
  const { 
    currentOrg, 
    availableOrgs, 
    switchOrganization, 
    loading 
  } = useOrganization();
  
  const { user, loading: userLoading } = useCurrentUser();
  const { isAdmin, isSuperAdmin } = useIsAdmin();
  const canAccessSettings = isAdmin || isSuperAdmin;
  const { data: supervision } = useSupervisionAccess();
  const hasSupervisionAccess = !!supervision?.hasAccess;
  const [showOrganizationsModal, setShowOrganizationsModal] = useState(false);
  const [pinnedOrgs, setPinnedOrgs] = useState<string[]>([]);

  console.log('🟠 [OrganizationSwitcher] render', { user: user?.email, userLoading, orgLoading: loading, currentOrg: currentOrg?.name });

  useEffect(() => {
    // Carregar organizações fixadas do localStorage
    const stored = localStorage.getItem('pinned_organizations');
    if (stored) {
      try {
        setPinnedOrgs(JSON.parse(stored));
      } catch {
        setPinnedOrgs([]);
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="h-8 w-8 bg-muted rounded-full animate-pulse" />
        <div className="h-4 w-24 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  if (!currentOrg) {
    return null;
  }

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'default';
      case 'manager':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'manager':
        return 'Gerente';
      case 'rep':
        return 'Rep';
      default:
        return role;
    }
  };

  // Filtrar organizações fixadas
  const pinnedOrganizations = availableOrgs.filter(org => pinnedOrgs.includes(org.id));
  const otherOrganizations = availableOrgs.filter(org => !pinnedOrgs.includes(org.id) && org.id !== currentOrg.id);

  const handleLogout = async () => {
    try {
      cleanupAuthState();
      try {
        await supabase.auth.signOut({
          scope: 'global' as any
        });
      } catch {}
    } finally {
      window.location.href = '/auth';
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            className="flex items-center space-x-2 h-auto p-2 hover:bg-hover transition-smooth"
          >
            {/* Avatar do usuário no lugar do ícone de organização */}
            <Avatar className="h-8 w-8 flex-shrink-0">
              <AvatarImage src={user?.avatar_url || "/api/placeholder/32/32"} alt="Avatar" />
              <AvatarFallback className="sidebar-avatar-fallback">
                {user?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col items-start">
              <span className="text-sm font-medium">
                {currentOrg.name}
              </span>
              <Badge variant={getRoleBadgeVariant(currentOrg.role)} className="text-xs h-4">
                {getRoleLabel(currentOrg.role)}
              </Badge>
            </div>
            <ChevronDown className="h-4 w-4 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-80 bg-popover border border-border shadow-lg max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col"
        >
          {/* Seção do usuário */}
          <div className="flex-shrink-0 p-3 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <AvatarImage src={user?.avatar_url || "/api/placeholder/32/32"} alt="Avatar" />
                  <AvatarFallback className="sidebar-avatar-fallback">
                    {user?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {user?.full_name || user?.email?.split('@')[0] || 'Usuário'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email || 'sem-email'}
                  </p>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    <span>Perfil</span>
                  </DropdownMenuItem>
                  {canAccessSettings && (
                    <DropdownMenuItem onClick={() => navigate('/settings')}>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Configurações</span>
                    </DropdownMenuItem>
                  )}
                  {hasSupervisionAccess && (
                    <DropdownMenuItem onClick={() => navigate('/supervisao')}>
                      <Eye className="mr-2 h-4 w-4" />
                      <span>Supervisão</span>
                    </DropdownMenuItem>
                  )}
                  {isSuperAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/super-admin')}>
                      <Shield className="mr-2 h-4 w-4" />
                      <span>Super Admin</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sair</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Organizações fixadas */}
          {pinnedOrganizations.length > 0 && (
            <>
              <div className="flex-shrink-0 px-3 pt-3">
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                  Organizações Fixadas
                </DropdownMenuLabel>
              </div>
              <div className="flex-shrink-0">
                {pinnedOrganizations.map((org) => (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={() => switchOrganization(org.id)}
                    className={cn(
                      "flex items-center justify-between cursor-pointer p-3",
                      currentOrg.id === org.id && "bg-muted"
                    )}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{org.name}</span>
                        <Badge variant={getRoleBadgeVariant(org.role)} className="text-xs h-4 self-start">
                          {getRoleLabel(org.role)}
                        </Badge>
                      </div>
                    </div>
                    {currentOrg.id === org.id && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
              </div>
            </>
          )}

          {/* Botão para ver todas as organizações */}
          <div className="flex-shrink-0 px-3 py-2 border-t">
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => setShowOrganizationsModal(true)}
            >
              <Building2 className="mr-2 h-4 w-4" />
              Ver todas as organizações
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <OrganizationsModal 
        open={showOrganizationsModal} 
        onOpenChange={setShowOrganizationsModal}
        onPinnedChange={(newPinned) => setPinnedOrgs(newPinned)}
      />
    </>
  );
};